import type { Bootstrap, Member, Preferences } from '@obv/api-client';
import { dimOpacity, effectiveScene, toUnit, zonedParts, type Scene } from '@obv/core';
import { applyTheme, familyTheme, isStandalone, resolveTokens, useClock, useIdle, useIsPhone, useOrientation, type ThemeKey } from '@obv/ui';
import { onlineManager } from '@tanstack/react-query';
import { Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { Route, Switch, useLocation } from 'wouter';
import { report } from '@/lib/errorReporter';
import { useReloadOnNewBuild } from '@/lib/buildWatcher';
import { lazyWithReload } from '@/lib/lazyWithReload';
import { useHideSplash } from '@/lib/splash';
import { useUpdatePreferences, useWeather } from '@/queries/hooks';
import { DisplayContext, type DisplayCtx } from './context';
import { CalendarIcon, ClockIcon, ExchangeIcon, HabitsIcon, KanjiIcon, PrayerIcon, QuranIcon, ScenesIcon, SettingsIcon, TasksIcon, TodayIcon } from './icons';
import { Ringer, useAudioUnlock } from './Ringer';
import { EveningView, FocusView } from './screens/Ambient';
import { CalendarScreen } from './screens/Calendar';
import { DashboardView, FamilyView } from './screens/Dashboard';
import { HabitsScreen } from './screens/Habits';
import { ScenesScreen } from './screens/Scenes';
import { TasksScreen } from './screens/Tasks';
import { TodayScreen } from './screens/Today';
import { markProbeDone, probeDone, sampleFrames, summarizeFrames } from './perfProbe';
import { useDisplayUi } from './store';

// Forms (react-hook-form + zod) load on first use, keeping them out of the iPad's initial bundle.
const PlanSheet = lazyWithReload(() => import('./PlanSheet'));
const TaskSheet = lazyWithReload(() => import('./TaskSheet'));
const HabitSheet = lazyWithReload(() => import('./HabitSheet'));
const AlarmSheet = lazyWithReload(() => import('./AlarmSheet'));
// Pages beyond the home screen load on first visit, keeping their code out of the iPad's initial bundle.
const QuranScreen = lazyWithReload(() => import('./screens/Quran'));
const ClockScreen = lazyWithReload(() => import('./screens/Clock'));
const PrayerScreen = lazyWithReload(() => import('./screens/Prayer'));
const SettingsScreen = lazyWithReload(() => import('./screens/Settings'));
const JapaneseScreen = lazyWithReload(() => import('./screens/Japanese'));
const ExchangeScreen = lazyWithReload(() => import('./screens/Exchange'));

const IDLE_HOME_MS = 5 * 60_000;
const CHROME_IDLE_MS = 10_000;
const INTERACTIVE = 'button, a, input, select, textarea, [role="button"], [role="checkbox"], [role="switch"], [role="radio"], [role="link"]';
const SHEET_IDLE_MS = 2 * 60_000;
const FOCUS_NAV_MS = 10_000;

function useOnline(): boolean {
  const [online, setOnline] = useState(onlineManager.isOnline());
  useEffect(() => onlineManager.subscribe(() => setOnline(onlineManager.isOnline())), []);
  return online;
}

export function DisplayShell({ boot, prefs }: { boot: Bootstrap; prefs: Preferences }) {
  const tz = boot.household.timezone;
  const clock = useClock(tz);
  const orientation = useOrientation();
  const phone = useIsPhone();
  const online = useOnline();
  const [location, navigate] = useLocation();
  const closeSheet = useDisplayUi((s) => s.closeSheet);
  const sheetOpen = useDisplayUi((s) => s.sheet !== null);
  const rootRef = useRef<HTMLDivElement>(null);

  const chosenAt = prefs.updated_at ? zonedParts(new Date(prefs.updated_at), tz) : null;
  const scene: Scene = effectiveScene(prefs, clock, chosenAt);
  // Evening switches to the dark palette unless the user picked E-ink; "Our family" brings its own
  // background, chosen in that scene's customizer.
  const theme: ThemeKey =
    scene === 'family'
      ? familyTheme(prefs.family_background)
      : scene === 'evening' && prefs.theme !== 'ink' && resolveTokens(prefs.theme, false).scheme === 'light'
        ? 'dark'
        : prefs.theme;

  useLayoutEffect(() => {
    if (rootRef.current) applyTheme(rootRef.current, theme, prefs.reduce_effects);
    const t = resolveTokens(theme, prefs.reduce_effects);
    document.body.style.background = t.bodyBg || (t.bg!.indexOf('gradient') >= 0 ? '#16408F' : t.bg!);
  }, [theme, prefs.reduce_effects]);

  usePerfProbe(theme, prefs.reduce_effects);
  useReloadOnNewBuild(IDLE_HOME_MS);
  useIdle(IDLE_HOME_MS, () => navigate('/display'), location !== '/display');
  useIdle(SHEET_IDLE_MS, closeSheet, sheetOpen);
  useAudioUnlock();
  useHideSplash();

  const membersById = useMemo(() => {
    const m: Record<string, Member> = {};
    for (const x of boot.members) m[x.id] = x;
    return m;
  }, [boot.members]);

  // Auto-hide menu: the nav rail (and the home header) stay out of the way until the edge tab or
  // an empty spot is tapped, then tuck away again after a few seconds without a touch. Widgets
  // link to their pages and every page has a Home button, so the rail is rarely needed.
  const editingHome = useDisplayUi((s) => s.homeDraft !== null);
  const [chromeShown, setChromeShown] = useState(false);
  const chromeHidden = prefs.auto_hide_menu && !chromeShown && !editingHome;
  useIdle(CHROME_IDLE_MS, () => setChromeShown(false), chromeShown && !editingHome && !sheetOpen);
  const showChrome = () => setChromeShown(true);
  const revealOnEmptyTap = (e: ReactMouseEvent) => {
    if (!chromeHidden) return;
    const t = e.target as HTMLElement;
    if (t.closest && t.closest(INTERACTIVE)) return;
    setChromeShown(true);
  };

  const ctx: DisplayCtx = { boot, prefs, tz, clock, orientation, phone, membersById, online, chromeHidden, showChrome };
  const focus = scene === 'focus' && location === '/display';
  const dim = dimOpacity(scene, clock.hhmm, prefs.morning_starts);
  const statusBar = isStandalone();

  return (
    <DisplayContext.Provider value={ctx}>
      <div ref={rootRef} className="display-root flex flex-col" data-scene={scene}>
        {statusBar ? <div className="h-[20px] flex-none" style={{ background: resolveTokens(theme, false).scheme === 'light' ? '#232521' : 'transparent' }} /> : null}
        {/* On a phone the nav is a bottom tab bar, so the row becomes a column with the nav last. */}
        <div className={`relative flex min-h-0 flex-1 ${phone ? 'flex-col-reverse' : ''}`}>
          {focus ? <FocusFrame /> : chromeHidden ? null : <NavRail />}
          {chromeHidden ? (
            <button
              type="button"
              aria-label="Show menu"
              onClick={showChrome}
              className="absolute left-0 top-1/2 z-10 -mt-[44px] flex h-[88px] w-[28px] items-center justify-start pl-[6px]"
            >
              <span className="h-[48px] w-[5px] rounded-full" style={{ background: 'var(--bd)' }} />
            </button>
          ) : null}
          {!focus ? (
            <main
              className={`flex min-h-0 min-w-0 flex-1 flex-col ${phone ? 'px-4 pb-3 pt-3' : chromeHidden ? 'px-6 pb-5 pt-4' : 'px-7 pb-[22px] pt-5'}`}
              onClickCapture={revealOnEmptyTap}
            >
              <Switch>
                <Route path="/display/calendar" component={CalendarScreen} />
                <Route path="/display/tasks" component={TasksScreen} />
                <Route path="/display/habits" component={HabitsScreen} />
                <Route path="/display/clock">
                  <Lazy>
                    <ClockScreen />
                  </Lazy>
                </Route>
                <Route path="/display/prayer">
                  <Lazy>
                    <PrayerScreen />
                  </Lazy>
                </Route>
                <Route path="/display/quran">
                  <Lazy>
                    <QuranScreen />
                  </Lazy>
                </Route>
                <Route path="/display/japanese">
                  <Lazy>
                    <JapaneseScreen />
                  </Lazy>
                </Route>
                <Route path="/display/exchange">
                  <Lazy>
                    <ExchangeScreen />
                  </Lazy>
                </Route>
                <Route path="/display/scenes">
                  <ScenesScreen active={scene} />
                </Route>
                <Route path="/display/settings">
                  <Lazy>
                    <SettingsScreen />
                  </Lazy>
                </Route>
                <Route>
                  <SceneHome scene={scene} />
                </Route>
              </Switch>
            </main>
          ) : null}
        </div>
        {sheetOpen ? (
          <Suspense fallback={null}>
            <SheetFor />
          </Suspense>
        ) : null}
        {dim > 0 ? <div className="cover pointer-events-none z-40" style={{ background: '#000', opacity: dim }} /> : null}
        <Ringer />
      </div>
    </DisplayContext.Provider>
  );
}

/** First launch on a device with a blurred theme: measure, and fall back to solid cards if slow. */
function usePerfProbe(theme: ThemeKey, reduceEffects: boolean) {
  const prefs = useUpdatePreferences();
  const mutate = prefs.mutate;
  useEffect(() => {
    if (reduceEffects || probeDone() || resolveTokens(theme, false).blur === 'none') return;
    let cancelled = false;
    sampleFrames(3000, 2500).then((deltas) => {
      if (cancelled) return;
      const stats = summarizeFrames(deltas);
      markProbeDone(stats);
      report('info', `perf-probe theme=${theme} frames=${stats.frames} median=${stats.medianMs}ms p90=${stats.p90Ms}ms slow=${stats.slow}`);
      if (stats.slow) mutate({ reduce_effects: true });
    });
    return () => {
      cancelled = true;
    };
  }, [theme, reduceEffects, mutate]);
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="flex flex-1 items-center justify-center text-[14px] text-muted">Opening…</div>}>{children}</Suspense>;
}

function SheetFor() {
  const mode = useDisplayUi((s) => (s.sheet ? s.sheet.mode : null));
  if (mode === 'addTask' || mode === 'editTask') return <TaskSheet />;
  if (mode === 'addHabit' || mode === 'editHabit') return <HabitSheet />;
  if (mode === 'addAlarm' || mode === 'editAlarm') return <AlarmSheet />;
  return <PlanSheet />;
}

function SceneHome({ scene }: { scene: Scene }) {
  if (scene === 'dashboard') return <DashboardView />;
  if (scene === 'family') return <FamilyView />;
  if (scene === 'evening') return <EveningView />;
  if (scene === 'outdoor') return <TodayScreen variant="outdoor" />;
  return <TodayScreen />;
}

/** Focus scene: full-screen clock; a tap reveals the nav rail for a few seconds. */
function FocusFrame() {
  const [showNav, setShowNav] = useState(false);
  useEffect(() => {
    if (!showNav) return;
    const t = setTimeout(() => setShowNav(false), FOCUS_NAV_MS);
    return () => clearTimeout(t);
  }, [showNav]);
  return (
    <>
      {showNav ? <NavRail /> : null}
      <div className="relative min-w-0 flex-1" onClick={() => setShowNav(true)}>
        <FocusView />
      </div>
    </>
  );
}

const NAV: { path: string; label: string; icon: (p: { size?: number }) => ReactNode }[] = [
  { path: '/display', label: 'Today', icon: TodayIcon },
  { path: '/display/calendar', label: 'Calendar', icon: CalendarIcon },
  { path: '/display/tasks', label: 'Chores', icon: TasksIcon },
  { path: '/display/habits', label: 'Habits', icon: HabitsIcon },
  { path: '/display/prayer', label: 'Prayer', icon: PrayerIcon },
  { path: '/display/quran', label: 'Quran', icon: QuranIcon },
  { path: '/display/clock', label: 'Clock', icon: ClockIcon },
  { path: '/display/exchange', label: 'Exchange', icon: ExchangeIcon },
  { path: '/display/japanese', label: 'Kanji', icon: KanjiIcon },
  { path: '/display/scenes', label: 'Scenes', icon: ScenesIcon },
  { path: '/display/settings', label: 'Settings', icon: SettingsIcon },
];

function NavRail() {
  const [location, navigate] = useLocation();
  const weather = useWeather();
  return (
    <DisplayContext.Consumer>
      {(ctx) =>
        ctx && ctx.phone ? (
          <nav
            className="scroll-x flex flex-none border-t border-line px-2 pt-[6px]"
            style={{ paddingBottom: 'calc(6px + env(safe-area-inset-bottom))' }}
            aria-label="Sections"
          >
            {NAV.map((n) => {
              const on = n.path === '/display' ? location === '/display' || location === '/display/' : location.indexOf(n.path) === 0;
              const Icon = n.icon;
              return (
                <button
                  key={n.path}
                  type="button"
                  aria-current={on ? 'page' : undefined}
                  onClick={() => navigate(n.path)}
                  className="tap mr-1 flex h-[52px] w-[64px] flex-none flex-col items-center justify-center rounded-[14px]"
                  style={{ background: on ? 'var(--ink)' : 'transparent', color: on ? 'var(--onInk)' : 'var(--muted2)' }}
                >
                  <Icon size={20} />
                  <span className="mt-[4px] text-[11px] font-medium tracking-[0.01em]">{n.label}</span>
                </button>
              );
            })}
          </nav>
        ) : (
        <nav className="flex w-[88px] flex-none flex-col items-center border-r border-line pb-[18px] pt-[14px]" aria-label="Sections">
          <div className="flex h-[40px] w-[56px] items-center justify-center rounded-[12px] font-serif text-[17px] tracking-[0.02em]" style={{ background: 'var(--ink)', color: 'var(--onInk)' }}>
            OBV
          </div>
          {/* my-auto centres the buttons yet still lets the list scroll from the top when it overflows. */}
          <div className="scroll-y flex min-h-0 flex-1 flex-col">
            <div className="my-auto py-2">
              {NAV.map((n, i) => {
              const on = n.path === '/display' ? location === '/display' || location === '/display/' : location.indexOf(n.path) === 0;
              const Icon = n.icon;
              return (
                <button
                  key={n.path}
                  type="button"
                  aria-current={on ? 'page' : undefined}
                  onClick={() => navigate(n.path)}
                  className="tap flex h-[54px] w-[68px] flex-none flex-col items-center justify-center rounded-[16px]"
                  style={{
                    marginTop: i ? 3 : 0,
                    background: on ? 'var(--ink)' : 'transparent',
                    color: on ? 'var(--onInk)' : 'var(--muted2)',
                  }}
                >
                  <Icon size={22} />
                  <span className="mt-[5px] text-[11px] font-medium tracking-[0.01em]">{n.label}</span>
                </button>
              );
            })}
            </div>
          </div>
          <div className="text-center text-[12px] leading-[1.4] text-muted">
            {ctx ? ctx.boot.household.location.name.split(',')[0] : ''}
            <br />
            {ctx && weather.data ? `${toUnit(weather.data.current.temp_c, ctx.prefs.unit)}°` : '—'}
          </div>
        </nav>
        )
      }
    </DisplayContext.Consumer>
  );
}
